import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Badge,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import io from "socket.io-client";
import { URL } from "../constants/url";
import colors from "../constants/colors";
import WhatsAppChatScreen from "../components/charcha/WhatsAppChatScreen";

const AdminCharchaScreen = ({ adminData }) => {
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [replies, setReplies] = useState({});
  const [loadingReplies, setLoadingReplies] = useState({});
  const [userCache, setUserCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [editTargetId, setEditTargetId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const socketRef = useRef(null);
  const initializedRef = useRef(false); // Add this ref to track initialization

  // Load unread messages from localStorage on component mount - ONLY ONCE
  useEffect(() => {
    if (initializedRef.current) return; // Prevent multiple initializations
    
    const storedUnreadMessages = localStorage.getItem('adminCharchaUnreadMessages');
    //console.log('🔍 [INIT] Loading unread messages from localStorage:', storedUnreadMessages);
    
    if (storedUnreadMessages) {
      try {
        const parsedUnreadMessages = JSON.parse(storedUnreadMessages);
        //console.log('✅ [INIT] Loaded unread messages from localStorage:', parsedUnreadMessages);
        setUnreadMessages(parsedUnreadMessages);
      } catch (error) {
        console.error('❌ [INIT] Error parsing stored unread messages:', error);
        localStorage.removeItem('adminCharchaUnreadMessages');
      }
    } else {
      //console.log('ℹ️ [INIT] No unread messages found in localStorage');
    }
    
    initializedRef.current = true;
  }, []);

  // Save unread messages to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(unreadMessages).length === 0) {
      //console.log('🔄 [SAVE] Unread messages is empty object, checking if we should save');
      // Don't save empty object if we're just initializing
      return;
    }
    
   // console.log('💾 [SAVE] Unread messages changed, saving to localStorage:', unreadMessages);
    localStorage.setItem('adminCharchaUnreadMessages', JSON.stringify(unreadMessages));
  }, [unreadMessages]);

  // Initialize socket connection
  useEffect(() => {
    socketRef.current = io(URL, {
      transports: ["websocket", "polling"],
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Socket event listeners
  useEffect(() => {
    if (!socketRef.current) return;

    const socket = socketRef.current;

    const handleNewQuestion = (question) => {
      setQuestions((prev) => {
        const exists = prev.find((q) => q._id === question._id);
        if (exists) return prev;
        return [question, ...prev];
      });
    };
const handleNewReply = async ({ questionId, replyId, reply }) => {

  if (reply.repliedBy && !userCache[reply.repliedBy._id]) {
    await fetchUserDetails(reply.repliedBy._id);
  }

  setReplies((prev) => {
    const currentReplies = prev[questionId] || [];

    if (!replyId) {
      const newReplies = [reply, ...currentReplies];
      return { ...prev, [questionId]: newReplies };
    }

    const addNestedReply = (repliesArray) => {
      return repliesArray.map((r) => {
        if (r._id === replyId) {
          return {
            ...r,
            children: r.children ? [reply, ...r.children] : [reply],
          };
        }
        if (r.children && r.children.length > 0) {
          return { ...r, children: addNestedReply(r.children) };
        }
        return r;
      });
    };

    const updatedReplies = addNestedReply(currentReplies);
    return { ...prev, [questionId]: updatedReplies };
  });

  // ✅ UPDATED: Update the question's last message AND re-sort the list
  setQuestions((prev) => {
    const updatedQuestions = prev.map((q) => {
      if (q._id === questionId) {
        return {
          ...q,
          lastMessage: reply.replyText,
          lastMessageTime: reply.createdAt,
        };
      }
      return q;
    });
    
    // Sort by lastMessageTime (most recent first)
    return updatedQuestions.sort((a, b) => {
      const timeA = new Date(a.lastMessageTime || a.createdAt);
      const timeB = new Date(b.lastMessageTime || b.createdAt);
      return timeB - timeA; // Most recent first
    });
  });

  // Mark as unread if the message is from another user AND we're not currently viewing this chat
  const isFromOtherUser = reply.repliedBy?._id !== adminData?._id;
  const isNotViewingChat = selectedQuestion?._id !== questionId;

  if (isFromOtherUser && isNotViewingChat) {
    setUnreadMessages((prev) => {
      const newCount = (prev[questionId] || 0) + 1;
      const updated = { ...prev, [questionId]: newCount };
     
      return updated;
    });
  } else {
    
  }
};

    socket.on("new_charcha_question", handleNewQuestion);
    socket.on("new_charcha_reply", handleNewReply);
    socket.on("connect", () => {
      //console.log("✅ [SOCKET] Connected to server");
    });
    socket.on("disconnect", () => {
      //console.log("❌ [SOCKET] Disconnected from server");
    });

    return () => {
      socket.off("new_charcha_question", handleNewQuestion);
      socket.off("new_charcha_reply", handleNewReply);
    };
  }, [userCache, selectedQuestion, adminData]);

  // Fetch all questions with last message info
const fetchQuestions = async () => {
  try {
    setLoading(true);
    //console.log('📡 [API] Fetching questions...');
    
    const res = await fetch(`${URL}/api/charcha/questions-with-last-message`);

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    const data = await res.json();
    if (data.success) {
      const questionsData = data.data || [];
      
      // ✅ UPDATED: Sort by lastMessageTime (most recent first)
      const sortedQuestions = questionsData.sort((a, b) => {
        const timeA = new Date(a.lastMessageTime || a.createdAt);
        const timeB = new Date(b.lastMessageTime || b.createdAt);
        return timeB - timeA; // Most recent first
      });
      
      //console.log('✅ [API] Fetched and sorted questions:', sortedQuestions.length);
      setQuestions(sortedQuestions);

      // Cache users from the response
      const newUserCache = {};
      sortedQuestions.forEach((question) => {
        if (question.user) {
          newUserCache[question.user._id] = question.user;
        }
        // Also cache askedBy user if available
        if (question.askedBy && typeof question.askedBy === "object") {
          newUserCache[question.askedBy._id] = question.askedBy;
        }
      });
      setUserCache(newUserCache);

      // ✅ FIXED: Don't modify unreadMessages here - just log current state
      //console.log('📊 [UNREAD] Current unread messages state:', unreadMessages);
      
    } else {
      throw new Error(data.message || "Failed to fetch questions");
    }
  } catch (error) {
    console.error("❌ [API] Error fetching questions:", error);
    setError("Failed to load questions. Please try again.");
  } finally {
    setLoading(false);
  }
};

  const fetchUserDetails = async (userId) => {
    if (!userId) return null;

    if (userCache[userId]) return userCache[userId];

    try {
      const res = await fetch(
        `${URL}/api/users/get-user-by-query?userId=${userId}`
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();

      if (data?.success && data?.data) {
        setUserCache((prev) => ({ ...prev, [userId]: data.data }));
        return data.data;
      } else {
        const fallbackUser = {
          _id: userId,
          name: "User",
          profilePicture: null,
        };
        setUserCache((prev) => ({ ...prev, [userId]: fallbackUser }));
        return fallbackUser;
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      const fallbackUser = {
        _id: userId,
        name: "User",
        profilePicture: null,
      };
      setUserCache((prev) => ({ ...prev, [userId]: fallbackUser }));
      return fallbackUser;
    }
  };

  const handleSelectQuestion = async (question) => {
    // Clear unread messages when opening
    setUnreadMessages((prev) => {
      const updated = { ...prev };
      if (updated[question._id]) delete updated[question._id];
      return updated;
    });

    setSelectedQuestion(question);

    if (!replies[question._id]) {
      setLoadingReplies((prev) => ({ ...prev, [question._id]: true }));
      try {
        const res = await fetch(
          `${URL}/api/charcha/get-question-with-replies/${question._id}`
        );

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        setReplies((prev) => ({ ...prev, [question._id]: data.replies || [] }));

        // Update user cache with reply users
        const newUserCache = { ...userCache };
        const updateUserCacheFromReplies = (repliesArray) => {
          repliesArray.forEach((reply) => {
            if (reply.repliedBy && !newUserCache[reply.repliedBy._id]) {
              newUserCache[reply.repliedBy._id] = reply.repliedBy;
            }
            if (reply.children && reply.children.length > 0) {
              updateUserCacheFromReplies(reply.children);
            }
          });
        };

        updateUserCacheFromReplies(data.replies || []);
        setUserCache(newUserCache);
      } catch (error) {
        console.error("Error fetching replies:", error);
        setError("Failed to load replies for this question.");
      } finally {
        setLoadingReplies((prev) => ({ ...prev, [question._id]: false }));
      }
    }
  };

  // Open edit dialog
  const openEditDialog = (question, e) => {
    if (e) e.stopPropagation();
    setEditTargetId(question._id);
    setEditContent(question.contentText || "");
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditTargetId(null);
    setEditContent("");
  };

  // Submit edit (admin)
  const submitEditQuestion = async () => {
    if (!editTargetId) return;
    try {
      const res = await fetch(`${URL}/api/admin/edit-charcha-question/${editTargetId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contentText: editContent }),
      });
      const data = await res.json();
      if (data.success) {
        // update local list
        setQuestions((prev) => prev.map((q) => (q._id === editTargetId ? { ...q, contentText: data.data.contentText } : q)));
        if (selectedQuestion && selectedQuestion._id === editTargetId) {
          setSelectedQuestion((prev) => ({ ...prev, contentText: data.data.contentText }));
        }
        setSuccessMessage("Question updated successfully");
        closeEditDialog();
      } else {
        setError(data.message || "Failed to edit question");
      }
    } catch (err) {
      console.error("Error editing question:", err);
      setError("Error editing question");
    }
  };

  // Delete flow
  const openDeleteDialog = (question, e) => {
    if (e) e.stopPropagation();
    setDeleteTargetId(question._id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  };

  const confirmDeleteQuestion = async () => {
    if (!deleteTargetId) return;
    try {
      const res = await fetch(`${URL}/api/admin/delete-charcha-question/${deleteTargetId}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (data.success) {
        setQuestions((prev) => prev.filter((q) => q._id !== deleteTargetId));
        if (selectedQuestion && selectedQuestion._id === deleteTargetId) setSelectedQuestion(null);
        setSuccessMessage("Question deleted successfully");
        closeDeleteDialog();
      } else {
        setError(data.message || "Failed to delete question");
      }
    } catch (err) {
      console.error("Error deleting question:", err);
      setError("Error deleting question");
    }
  };

  const handleBackToList = () => {
    setSelectedQuestion(null);
  };

  const handleNewReply = async (questionId, replyText, parentReply = null) => {
    if (!replyText.trim()) return;

    try {
      const adminUserId = adminData?._id;
      if (!adminUserId) {
        setError("Admin user ID not found. Please log in again.");
        return;
      }

      const replyData = {
        questionId,
        replyId: parentReply?._id || null,
        replyText: replyText.trim(),
        userId: adminUserId,
      };

      const apiUrl = `${URL}/api/charcha/give-reply`;

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(replyData),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const result = await res.json();

      if (!result.success) {
        setError(result.message || "Failed to send reply.");
      }
    } catch (error) {
      console.error("Error sending reply:", error);
      setError(`Failed to send reply: ${error.message}`);
    }
  };

  // Format time for last message
  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format date for sidebar
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return formatTime(timestamp);
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  // Truncate text with ellipsis
  const truncateText = (text, maxLength = 30) => {
    if (!text) return "No messages yet";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Close error snackbar
  const handleCloseError = () => {
    setError(null);
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

   return (
    <Box
      sx={{
        height: "100vh", // CHANGE to 100vh
        display: "flex",
        flexDirection: "column",
        background: colors.white,
        overflow: "hidden", // ADD THIS
      }}
    >
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Admin Panel Header - Only show when NOT in chat screen */}
      {!selectedQuestion && (
        <AppBar
          position="sticky"
          sx={{
            background: "#279450",
            borderRadius: "0px",
            zIndex: 1200,
          }}
        >
          <Toolbar>
            <AdminPanelSettingsIcon sx={{ mr: 2, color: colors.white }} />
            <Typography
              variant="h6"
              component="div"
              sx={{ flexGrow: 1, fontWeight: 600, color: colors.white }}
            >
            Charcha 
            </Typography>
            <Badge
              badgeContent={questions.length}
              color="error"
              sx={{
                "& .MuiBadge-badge": {
                  background: colors.orange,
                  color: colors.white,
                },
              }}
            >
              <Typography variant="body2" sx={{ color: colors.white }}>
                Questions
              </Typography>
            </Badge>
          </Toolbar>
        </AppBar>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={closeEditDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Question</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            variant="outlined"
            label="Question"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancel</Button>
          <Button onClick={submitEditQuestion} variant="contained" sx={{ backgroundColor: colors.green }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Delete Question</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this question and all its replies?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={confirmDeleteQuestion} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          height: selectedQuestion ? "100vh" : "calc(100vh - 64px)",
          overflow: "hidden",
        }}
      >
        {/* Questions List Sidebar */}
        {!selectedQuestion ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflow: "auto",
              background: colors.white,
              // scrollbarWidth: "none",
              // msOverflowStyle: "none",
              // "&::-webkit-scrollbar": {
              //   display: "none",
              // },
            }}
          >
            <List sx={{ p: 0 }}>
              {questions.length === 0 ? (
                <Box sx={{ textAlign: "center", p: 4, mt: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    No questions yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Questions from users will appear here
                  </Typography>
                </Box>
              ) : (
                questions.map((question, index) => {
                  const user =
                    userCache[question.askedBy] ||
                    question.user ||
                    question.askedBy;
                  const hasUnread = unreadMessages[question._id] > 0;
                  const unreadCount = unreadMessages[question._id] || 0;

                  return (
                    <React.Fragment key={question._id}>
                      <ListItem
                        button
                        onClick={() => handleSelectQuestion(question)}
                        sx={{
                          "&:hover": { background: "#f5f5f5" },
                          py: 2,
                          px: 2,
                          alignItems: "flex-start",
                          transition: "background-color 0.2s",
                          position: "relative",
                        }}
                      >
                        <ListItemAvatar sx={{ mr: 2 }}>
                          <Avatar
                            src={user?.profilePicture}
                            alt={user?.name || "User"}
                            sx={{
                              bgcolor: colors.darkGreen,
                              width: 52,
                              height: 52,
                            }}
                          >
                            {user?.name
                              ? user.name.charAt(0).toUpperCase()
                              : "U"}
                          </Avatar>
                        </ListItemAvatar>

                        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
                          {/* Header */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: hasUnread ? 700 : 600,
                                  fontSize: "1rem",
                                  color: hasUnread ? "#000" : "#333",
                                  lineHeight: 1.2,
                                  flex: 1,
                                  mr: 1,
                                }}
                              >
                                {question.contentText}
                              </Typography>

                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontSize: "0.75rem",
                                    whiteSpace: "nowrap",
                                    fontWeight: hasUnread ? 600 : 400,
                                  }}
                                >
                                  {formatDate(question.lastMessageTime)}
                                </Typography>

                                <Box>
                                  <Tooltip title="Edit question">
                                    <IconButton size="small" onClick={(e) => openEditDialog(question, e)} sx={{ mr: 0.5 }}>
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>

                                  <Tooltip title="Delete question">
                                    <IconButton size="small" onClick={(e) => openDeleteDialog(question, e)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </Box>
                          </Box>

                          {/* Last message row with badge immediately after text */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              width: "100%",
                              minHeight: '24px',
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: hasUnread ? "#000" : "rgba(0, 0, 0, 0.6)",
                                fontWeight: hasUnread ? 600 : 400,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                flex: 1,
                                maxWidth: hasUnread ? 'calc(100% - 28px)' : '100%',
                              }}
                            >
                              {truncateText(question.lastMessage)}
                            </Typography>

                            {/* Badge positioned immediately after the text */}
                            {hasUnread && (
                              <Box
                                sx={{
                                  background: "#25D366",
                                  color: "#fff",
                                  borderRadius: "50%",
                                  minWidth: 20,
                                  height: 20,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  flexShrink: 0,
                                }}
                              >
                                {unreadCount}
                              </Box>
                            )}
                          </Box>

                          {/* Asked by - aligned to right */}
                          <Box
                            sx={{ 
                              display: "flex", 
                              justifyContent: "flex-end",
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ 
                                fontSize: "0.75rem",
                                mt:-1
                              }}
                            >
                              Asked by: {user?.name || "Unknown User"}
                            </Typography>
                          </Box>
                        </Box>
                      </ListItem>

                      {index < questions.length - 1 && (
                        <Divider
                          variant="inset"
                          component="li"
                          sx={{ ml: 8 }}
                        />
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </List>
          </Box>
        ) : (
          /* Chat Screen */
          <Box sx={{ flex: 1, width: "100%", height: "100vh" }}>
            <WhatsAppChatScreen
              question={selectedQuestion}
              user={
                userCache[selectedQuestion.askedBy] || selectedQuestion.user
              }
              replies={replies[selectedQuestion._id] || []}
              loadingReplies={loadingReplies[selectedQuestion._id]}
              onSendReply={handleNewReply}
              currentUser={adminData}
              onBack={handleBackToList}
              onOpenEdit={openEditDialog}
              onOpenDelete={openDeleteDialog}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdminCharchaScreen;